import { Component, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';

import { RouterModule, RouterLink } from '@angular/router';
import { OfferWithImage } from '../../model/offer-with-image';
import { OfferCardComponent } from '../components/offer-card/offer-card.component';
import { SkeletonLoadingComponent } from '../components/skeleton-loading/skeleton-loading.component';
import { UserService } from '@/service/user.service';
import { ManagedUser } from '@/model/user';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BreakpointQuery } from '@/model/breakpoint-query';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { KeycloakService } from 'keycloak-angular';
import { User } from '@/model/user';
import { HomeFacade } from './home.facade';
import { combineLatest, of, map } from 'rxjs';
import { TrendingCategoriesFacade } from '../store/trending-categories';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
@UntilDestroy()
@Component({
  selector: 'app-home',
  imports: [
    RouterModule,
    NgClass,
    OfferCardComponent,
    SkeletonLoadingComponent,
    CommonModule,
    RouterLink,
    MatExpansionModule,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [HomeFacade],
  standalone: true,
})
export class HomeComponent implements OnInit {
  selectedTab = 0;
  // Observables for template via async pipe
  readonly isLoading$ = this.homeFacade.isLoading$;
  readonly parentCategories$ = this.homeFacade.parentCategories$;
  readonly filteredOffers$ = this.homeFacade.filteredOffers$;
  readonly userOffers$ = this.homeFacade.userOffers$;
  readonly managedUsers$ = this.homeFacade.managedUsers$;
  readonly managedUserOffers$ = this.homeFacade.managedUsersOffers$;
  readonly user$ = this.userService.user$;
  user: User | null = null;
  isLoggedIn = false;
  isMobileView = false;
  expandedPanelIndex = -1;

  // Derived visibility for Trending section
  readonly showTrending$ = combineLatest([
    of(this.keycloakService.isLoggedIn()),
    this.homeFacade.managedUsers$,
    this.userService.user$,
  ]).pipe(
    map(([loggedIn, managedUsers, user]) =>
      this.trendingCategoriesFacade.shouldShowTrending(
        loggedIn,
        managedUsers,
        user,
      ),
    ),
  );

  constructor(
    private keycloakService: KeycloakService,
    private userService: UserService,
    private breakpointObserver: BreakpointObserver,
    private homeFacade: HomeFacade,
    private trendingCategoriesFacade: TrendingCategoriesFacade,
  ) {}

  ngOnInit(): void {
    // Check if user is logged in and load managed users via facade
    this.isLoggedIn = this.keycloakService.isLoggedIn();
    this.userService.user$
      .pipe(untilDestroyed(this))
      .subscribe((user) => (this.user = user));
    // Initial data loads are triggered by facade constructor

    this.breakpointObserver
      .observe(BreakpointQuery.Compact)
      .pipe(untilDestroyed(this))
      .subscribe((state) => {
        this.isMobileView = state.matches;
        // Reset expanded panel when switching to mobile view
        if (state.matches) {
          this.expandedPanelIndex = -1;
        }
      });
  }

  onCategorySelect(categoryId: string, index: number): void {
    this.selectedTab = index;
    this.expandedPanelIndex = index;
    this.homeFacade.selectCategory(categoryId);
  }

  filterOffersByCategory(categoryId: string): void {
    this.homeFacade.selectCategory(categoryId);
  }

  getTopThreeOffers(offers: OfferWithImage[]): OfferWithImage[] {
    return offers.slice(0, 3);
  }

  getTopOffers(offers: OfferWithImage[], count = 4): OfferWithImage[] {
    return offers.slice(0, count);
  }

  getManagedUserCategoriesAsQueryParam(user: ManagedUser): string {
    return user.categories
      .map((cat) => `${cat.id}:${cat.name}:category`)
      .join(',');
  }

  getUserCategoriesAsQueryParam(user: User): string {
    if (!user || !user.categories) return '';
    return user.categories
      .map((cat) => `${cat.id}:${cat.name}:category`)
      .join(',');
  }
}
